'use client';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type {
  SeasonDto, NutTypeDto, RegionDto, PaymentMethodDto, PaymentTypeDto, OriginDto, ConversionFactorDto,
  MovementTypeDto, RoutePointDto, VesselDto, CountryDto, StateDto, MunicipalityDto, MovementKindDto, GroupingDto,
  BankAccountDto,
} from '@/types/api';

// Os seletores (criar contrato) querem só os itens ATIVOS; as telas de Configuração
// precisam ver os inativos também — senão, ao desativar um item, ele sumiria da
// própria tela que o gerencia e não haveria como reativá-lo.
//
// Por isso `activeOnly` é parâmetro e entra na queryKey: as duas listas coexistem em
// cache sem uma sobrescrever a outra.

function query(path: string) {
  return (activeOnly: boolean) =>
    api.get(`/ReferenceData/${path}${activeOnly ? '?activeOnly=true' : ''}`).then((r) => r.data);
}

export function useSeasons(activeOnly = true) {
  return useQuery<SeasonDto[]>({
    queryKey: ['seasons', activeOnly],
    queryFn: () => query('seasons')(activeOnly),
  });
}

export function useNutTypes(activeOnly = true) {
  return useQuery<NutTypeDto[]>({
    queryKey: ['nut-types', activeOnly],
    queryFn: () => query('nut-types')(activeOnly),
  });
}

export function useRegions(activeOnly = true) {
  return useQuery<RegionDto[]>({
    queryKey: ['regions', activeOnly],
    queryFn: () => query('regions')(activeOnly),
  });
}

export function usePaymentMethods(activeOnly = true) {
  return useQuery<PaymentMethodDto[]>({
    queryKey: ['payment-methods', activeOnly],
    queryFn: () => query('payment-methods')(activeOnly),
  });
}

export function usePaymentTypes(activeOnly = true) {
  return useQuery<PaymentTypeDto[]>({
    queryKey: ['payment-types', activeOnly],
    queryFn: () => query('payment-types')(activeOnly),
  });
}

export function useOrigins(regionId?: string, activeOnly = false) {
  return useQuery<OriginDto[]>({
    queryKey: ['origins', regionId, activeOnly],
    queryFn: () => api.get('/ReferenceData/origins', {
      params: { regionId, activeOnly: activeOnly ? true : undefined },
    }).then((r) => r.data),
  });
}

// Toda mutação invalida a chave inteira (sem o activeOnly), então as duas variações
// da lista se atualizam juntas. Invalidar só uma delas deixaria a outra mentindo.
function useRefMutation<T>(chaves: string[], fn: (vars: T) => Promise<unknown>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => chaves.forEach((k) => qc.invalidateQueries({ queryKey: [k] })),
  });
}

// ---- safras ----

export function useCreateSeason() {
  return useRefMutation<{ name: string; year: number }>(['seasons'], (v) =>
    api.post('/ReferenceData/seasons', v));
}

export function useUpdateSeason() {
  return useRefMutation<{ id: string; name: string; year: number; isActive: boolean }>(['seasons'], (v) =>
    api.put(`/ReferenceData/seasons/${v.id}`, { name: v.name, year: v.year, isActive: v.isActive }));
}

export function useDeleteSeason() {
  return useRefMutation<string>(['seasons'], (id) => api.delete(`/ReferenceData/seasons/${id}`));
}

// ---- regiões ----
// Invalidam 'origins' junto: a origem carrega o nome da região, que acabou de mudar.

export function useCreateRegion() {
  return useRefMutation<{ name: string }>(['regions', 'origins'], (v) =>
    api.post('/ReferenceData/regions', v));
}

export function useUpdateRegion() {
  return useRefMutation<{ id: string; name: string; isActive: boolean }>(['regions', 'origins'], (v) =>
    api.put(`/ReferenceData/regions/${v.id}`, { name: v.name, isActive: v.isActive }));
}

export function useDeleteRegion() {
  return useRefMutation<string>(['regions', 'origins'], (id) => api.delete(`/ReferenceData/regions/${id}`));
}

// ---- tipos de castanha ----

export function useCreateNutType() {
  return useRefMutation<{ name: string }>(['nut-types'], (v) =>
    api.post('/ReferenceData/nut-types', v));
}

export function useUpdateNutType() {
  return useRefMutation<{ id: string; name: string; isActive: boolean }>(['nut-types'], (v) =>
    api.put(`/ReferenceData/nut-types/${v.id}`, { name: v.name, isActive: v.isActive }));
}

export function useDeleteNutType() {
  return useRefMutation<string>(['nut-types'], (id) => api.delete(`/ReferenceData/nut-types/${id}`));
}

// ---- formas de pagamento ----

export function useCreatePaymentMethod() {
  return useRefMutation<{ name: string }>(['payment-methods'], (v) =>
    api.post('/ReferenceData/payment-methods', v));
}

export function useUpdatePaymentMethod() {
  return useRefMutation<{ id: string; name: string; isActive: boolean }>(['payment-methods'], (v) =>
    api.put(`/ReferenceData/payment-methods/${v.id}`, { name: v.name, isActive: v.isActive }));
}

export function useDeletePaymentMethod() {
  return useRefMutation<string>(['payment-methods'], (id) =>
    api.delete(`/ReferenceData/payment-methods/${id}`));
}

// ---- tipos de pagamento ----

export function useCreatePaymentType() {
  return useRefMutation<{ name: string }>(['payment-types'], (v) =>
    api.post('/ReferenceData/payment-types', v));
}

export function useUpdatePaymentType() {
  return useRefMutation<{ id: string; name: string; isActive: boolean }>(['payment-types'], (v) =>
    api.put(`/ReferenceData/payment-types/${v.id}`, { name: v.name, isActive: v.isActive }));
}

export function useDeletePaymentType() {
  return useRefMutation<string>(['payment-types'], (id) =>
    api.delete(`/ReferenceData/payment-types/${id}`));
}

// ---- origens da mercadoria ----

export function useCreateOrigin() {
  return useRefMutation<{ name: string; river?: string | null; regionId: string }>(['origins', 'regions'], (v) =>
    api.post('/ReferenceData/origins', v));
}

export function useUpdateOrigin() {
  return useRefMutation<{ id: string; name: string; river?: string | null; regionId: string; isActive: boolean }>(
    ['origins', 'regions'], (v) =>
      api.put(`/ReferenceData/origins/${v.id}`, { name: v.name, river: v.river, regionId: v.regionId, isActive: v.isActive }));
}

export function useDeleteOrigin() {
  return useRefMutation<string>(['origins', 'regions'], (id) => api.delete(`/ReferenceData/origins/${id}`));
}

// ---- fatores de conversão (Hectolitro -> Lata/Caixa) ----

export function useConversionFactors() {
  return useQuery<ConversionFactorDto[]>({
    queryKey: ['conversion-factors'],
    queryFn: () => api.get('/ReferenceData/conversion-factors').then((r) => r.data),
  });
}

export function useUpdateConversionFactor() {
  return useRefMutation<{ code: string; factor: number }>(['conversion-factors'], (v) =>
    api.put(`/ReferenceData/conversion-factors/${v.code}`, { factor: v.factor }));
}

// ---- tipos de movimentação (conta-corrente) ----

export function useMovementTypes(activeOnly = true) {
  return useQuery<MovementTypeDto[]>({
    queryKey: ['movement-types', activeOnly],
    queryFn: () => query('movement-types')(activeOnly),
  });
}

export function useCreateMovementType() {
  return useRefMutation<{ name: string; nature: 1 | 2 }>(['movement-types'], (v) =>
    api.post('/ReferenceData/movement-types', v));
}

export function useUpdateMovementType() {
  return useRefMutation<{ id: string; name: string; nature: 1 | 2; isActive: boolean }>(['movement-types'], (v) =>
    api.put(`/ReferenceData/movement-types/${v.id}`, { name: v.name, nature: v.nature, isActive: v.isActive }));
}

export function useDeleteMovementType() {
  return useRefMutation<string>(['movement-types'], (id) =>
    api.delete(`/ReferenceData/movement-types/${id}`));
}

// ---- pontos de rota (localidade/cidade/porto) ----

export function useRoutePoints(activeOnly = false) {
  return useQuery<RoutePointDto[]>({
    queryKey: ['route-points', activeOnly],
    queryFn: () => query('route-points')(activeOnly),
  });
}

export function useCreateRoutePoint() {
  return useRefMutation<{ name: string }>(['route-points'], (v) =>
    api.post('/ReferenceData/route-points', v));
}

export function useUpdateRoutePoint() {
  return useRefMutation<{ id: string; name: string; isActive: boolean }>(['route-points'], (v) =>
    api.put(`/ReferenceData/route-points/${v.id}`, { name: v.name, isActive: v.isActive }));
}

export function useDeleteRoutePoint() {
  return useRefMutation<string>(['route-points'], (id) =>
    api.delete(`/ReferenceData/route-points/${id}`));
}

// ---- embarcações ----

export function useVessels(activeOnly = false) {
  return useQuery<VesselDto[]>({
    queryKey: ['vessels', activeOnly],
    queryFn: () => query('vessels')(activeOnly),
  });
}

export function useCreateVessel() {
  return useRefMutation<{ name: string; carrierId: string }>(['vessels'], (v) =>
    api.post('/ReferenceData/vessels', v));
}

export function useUpdateVessel() {
  return useRefMutation<{ id: string; name: string; carrierId: string; isActive: boolean }>(['vessels'], (v) =>
    api.put(`/ReferenceData/vessels/${v.id}`, { name: v.name, carrierId: v.carrierId, isActive: v.isActive }));
}

export function useDeleteVessel() {
  return useRefMutation<string>(['vessels'], (id) =>
    api.delete(`/ReferenceData/vessels/${id}`));
}

// ---- hierarquia geográfica: países ----

export function useCountries(activeOnly = false) {
  return useQuery<CountryDto[]>({
    queryKey: ['countries', activeOnly],
    queryFn: () => query('countries')(activeOnly),
  });
}

export function useCreateCountry() {
  return useRefMutation<{ name: string }>(['countries'], (v) =>
    api.post('/ReferenceData/countries', v));
}

export function useUpdateCountry() {
  return useRefMutation<{ id: string; name: string; isActive: boolean }>(['countries'], (v) =>
    api.put(`/ReferenceData/countries/${v.id}`, { name: v.name, isActive: v.isActive }));
}

export function useDeleteCountry() {
  return useRefMutation<string>(['countries'], (id) => api.delete(`/ReferenceData/countries/${id}`));
}

// ---- hierarquia geográfica: estados ----
// activeOnly entra na queryKey do mesmo jeito que as demais; countryId também, para
// a tela de Município poder listar os estados de um país específico sem colidir com
// a lista completa usada na tela de Estado.

export function useStates(countryId?: string, activeOnly = false) {
  return useQuery<StateDto[]>({
    queryKey: ['states', countryId, activeOnly],
    queryFn: () => api.get('/ReferenceData/states', {
      params: { countryId, activeOnly: activeOnly ? true : undefined },
    }).then((r) => r.data),
  });
}

export function useCreateState() {
  return useRefMutation<{ name: string; uf: string; countryId: string }>(['states'], (v) =>
    api.post('/ReferenceData/states', v));
}

export function useUpdateState() {
  return useRefMutation<{ id: string; name: string; uf: string; countryId: string; isActive: boolean }>(
    ['states'], (v) =>
      api.put(`/ReferenceData/states/${v.id}`,
        { name: v.name, uf: v.uf, countryId: v.countryId, isActive: v.isActive }));
}

export function useDeleteState() {
  return useRefMutation<string>(['states'], (id) => api.delete(`/ReferenceData/states/${id}`));
}

// ---- hierarquia geográfica: municípios ----

export function useMunicipalities(stateId?: string, activeOnly = false) {
  return useQuery<MunicipalityDto[]>({
    queryKey: ['municipalities', stateId, activeOnly],
    queryFn: () => api.get('/ReferenceData/municipalities', {
      params: { stateId, activeOnly: activeOnly ? true : undefined },
    }).then((r) => r.data),
  });
}

export function useCreateMunicipality() {
  return useRefMutation<{ name: string; stateId: string }>(['municipalities'], (v) =>
    api.post('/ReferenceData/municipalities', v));
}

export function useUpdateMunicipality() {
  return useRefMutation<{ id: string; name: string; stateId: string; isActive: boolean }>(
    ['municipalities'], (v) =>
      api.put(`/ReferenceData/municipalities/${v.id}`, { name: v.name, stateId: v.stateId, isActive: v.isActive }));
}

export function useDeleteMunicipality() {
  return useRefMutation<string>(['municipalities'], (id) => api.delete(`/ReferenceData/municipalities/${id}`));
}

// ---- tipo de movimento ----

export function useMovementKinds(activeOnly = false) {
  return useQuery<MovementKindDto[]>({
    queryKey: ['movement-kinds', activeOnly],
    queryFn: () => query('movement-kinds')(activeOnly),
  });
}

export function useCreateMovementKind() {
  return useRefMutation<{ name: string; countsTowardBalance: boolean }>(['movement-kinds'], (v) =>
    api.post('/ReferenceData/movement-kinds', v));
}

export function useUpdateMovementKind() {
  return useRefMutation<{ id: string; name: string; isActive: boolean; countsTowardBalance: boolean }>(['movement-kinds'], (v) =>
    api.put(`/ReferenceData/movement-kinds/${v.id}`, { name: v.name, isActive: v.isActive, countsTowardBalance: v.countsTowardBalance }));
}

export function useDeleteMovementKind() {
  return useRefMutation<string>(['movement-kinds'], (id) => api.delete(`/ReferenceData/movement-kinds/${id}`));
}

// ---- agrupamento ----

export function useGroupings(activeOnly = false) {
  return useQuery<GroupingDto[]>({
    queryKey: ['groupings', activeOnly],
    queryFn: () => query('groupings')(activeOnly),
  });
}

export function useCreateGrouping() {
  return useRefMutation<{ name: string }>(['groupings'], (v) =>
    api.post('/ReferenceData/groupings', v));
}

export function useUpdateGrouping() {
  return useRefMutation<{ id: string; name: string; isActive: boolean }>(['groupings'], (v) =>
    api.put(`/ReferenceData/groupings/${v.id}`, { name: v.name, isActive: v.isActive }));
}

export function useDeleteGrouping() {
  return useRefMutation<string>(['groupings'], (id) => api.delete(`/ReferenceData/groupings/${id}`));
}

// ---- contas bancárias avulsas (sem fornecedor — pagamento de Despesas Acessórias) ----

export function useBankAccounts(activeOnly = false) {
  return useQuery<BankAccountDto[]>({
    queryKey: ['bank-accounts', activeOnly],
    queryFn: () => query('bank-accounts')(activeOnly),
  });
}

export function useCreateBankAccount() {
  return useRefMutation<{
    bankName: string; bankCode?: string; agency?: string; accountNumber?: string;
    pixKey?: string; favoredName: string; favoredTaxId?: string;
  }>(['bank-accounts'], (v) => api.post('/ReferenceData/bank-accounts', v));
}

export function useUpdateBankAccount() {
  return useRefMutation<{
    id: string; bankName: string; bankCode?: string; agency?: string; accountNumber?: string;
    pixKey?: string; favoredName: string; favoredTaxId?: string; isActive: boolean;
  }>(['bank-accounts'], (v) => api.put(`/ReferenceData/bank-accounts/${v.id}`, v));
}

export function useDeactivateBankAccount() {
  return useRefMutation<string>(['bank-accounts'], (id) => api.delete(`/ReferenceData/bank-accounts/${id}`));
}
